import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SimpleLanguageProvider } from './contexts/SimpleLanguageContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { LoggerProvider } from './contexts/LoggerContext';
import './styles/theme.css';
import './styles/mobile.css';
import Home from './components/Home';
import MovieDetail from './components/MovieDetail';
import PersonDetail from './components/PersonDetail';
import GameDetail from './components/GameDetail';
import CastCrew from './components/CastCrew';
import About from './pages/About';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Help from './pages/Help';
import Settings from './pages/Settings';
import ProfileSettings from './pages/ProfileSettings';
import Notifications from './pages/Notifications';
import AdminDashboard from './pages/AdminDashboard';
import AdminNotifications from './pages/AdminNotifications';
import AdminEmail from './pages/AdminEmail';
import Watchlist from './pages/Watchlist';
import ResetPassword from './components/ResetPassword';
import ConfirmPasswordChange from './components/ConfirmPasswordChange';
import VerifyEmail from './components/VerifyEmail';
import EmailVerificationHelper from './components/EmailVerificationHelper';
import ProtectedRoute from './components/ProtectedRoute';
import Insider from './components/Insider';
import InsiderProject from './components/InsiderProject';
import InsiderSubmit from './pages/InsiderSubmit';
import AdminInsiderSubmissions from './pages/AdminInsiderSubmissions';
import AdminFeeds from './pages/AdminFeeds';
import CompanyDetail from './components/CompanyDetail';
import Sports from './components/Sports';
import Optimiser from './components/Optimiser';
import Calendar from './components/Calendar';
import Markets from './components/Markets';
import BrowseList from './components/BrowseList';
import Hub from './pages/Hub';
import HubDetail from './pages/HubDetail';
import { HUBS, HUB_DETAILS } from './config/hubs';

const VideBitsApp = () => {
  return (
    <LoggerProvider>
      <AuthProvider>
        <ThemeProvider>
          <SimpleLanguageProvider>
            <NotificationProvider>
            <Router>
            <Routes>
              {/* Home is search + calendar: the search box stays the front
                  door, with the release calendar underneath so the page shows
                  what's happening to someone who arrived without a query. A
                  calendar alone made a poor homepage — a month grid is a lookup
                  tool, useful only once you already have a title in mind. The
                  standalone grid remains at /calendar. */}
              <Route path="/" element={<Home />} />
              <Route path="/movie/:id" element={<MovieDetail />} />
              <Route path="/movie/:id/cast-crew" element={<CastCrew />} />
              <Route path="/tv/:id" element={<MovieDetail />} />
              <Route path="/tv/:id/cast-crew" element={<CastCrew />} />
              <Route path="/person/:id" element={<PersonDetail />} />
              {/* Public: a game has no TMDb id, so this is the only detail page
                  it has, and the calendar links every game here. */}
              <Route path="/game/:id" element={<GameDetail />} />
              {/* Premium features — require login */}
              <Route path="/optimiser" element={<Optimiser />} />
              {/* Public release calendar (new releases, theatrical, streaming);
                  the 'Watchlist' view inside it requires login. */}
              <Route path="/calendar" element={<Calendar />} />
              {/* Public: film budgets, revenue and return, from the event graph's TMDb data. */}
              <Route path="/markets" element={<Markets />} />
              <Route path="/browse" element={<BrowseList />} />
              {/* Catalogue hubs — one component, config per hub. */}
              <Route path="/franchises" element={<Hub hub={HUBS.franchises} />} />
              <Route path="/genres" element={<Hub hub={HUBS.genres} />} />
              <Route path="/studios" element={<Hub hub={HUBS.studios} />} />
              <Route path="/people" element={<Hub hub={HUBS.people} />} />
              <Route path="/franchise/:id" element={<HubDetail kind={HUB_DETAILS.franchise} />} />
              <Route path="/genre/:slug" element={<HubDetail kind={HUB_DETAILS.genre} />} />
              <Route path="/sports" element={<Sports />} />
              {/* Insider radar is a public taster; only the project detail is gated. */}
              <Route path="/insider" element={<Insider />} />
              <Route path="/insider/submit" element={<ProtectedRoute><InsiderSubmit /></ProtectedRoute>} />
              <Route path="/insider/project/:id" element={<ProtectedRoute><InsiderProject /></ProtectedRoute>} />
              <Route path="/company/:id" element={<CompanyDetail />} />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <ProfileSettings />
                </ProtectedRoute>
              } />
              <Route path="/watchlist" element={
                <ProtectedRoute>
                  <Watchlist />
                </ProtectedRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/notifications" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminNotifications />
                </ProtectedRoute>
              } />
              <Route path="/admin/email" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminEmail />
                </ProtectedRoute>
              } />
              <Route path="/admin/insider" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminInsiderSubmissions />
                </ProtectedRoute>
              } />
              <Route path="/admin/feeds" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminFeeds />
                </ProtectedRoute>
              } />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/confirm-password-change" element={<ConfirmPasswordChange />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/email-helper" element={<EmailVerificationHelper />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/help" element={<Help />} />
            </Routes>
            </Router>
            </NotificationProvider>
          </SimpleLanguageProvider>
        </ThemeProvider>
      </AuthProvider>
    </LoggerProvider>
  );
};

export default VideBitsApp;
